import React, { useState, useEffect } from 'react';
import { FileText } from 'lucide-react';
import { useAuth } from '../../../auth';
import { PRIMITIVES, API_BASE } from '../constants';
import { LogsTabSkeleton } from '../shared/Skeleton';

export default function LogsTab() {
  const { getToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    primitiveId: '',
    statusCode: '',
  });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    async function fetchLogs() {
      setLoading(true);
      try {
        const token = await getToken();
        const params = new URLSearchParams();
        params.set('page', filters.page);
        if (filters.primitiveId) params.set('primitive_id', filters.primitiveId);
        if (filters.statusCode) params.set('status_code', filters.statusCode);

        const res = await fetch(`${API_BASE}/logs?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setLogs(data.logs || []);
          setTotalPages(Math.ceil((data.total || 0) / (data.per_page || 50)));
        }
      } catch (err) {
        console.error('Failed to fetch logs:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchLogs();
  }, [getToken, filters]);

  function formatTimestamp(dateStr) {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  function formatCost(costCents) {
    if (!costCents || costCents === 0) return '$0.00';
    return `$${(costCents / 100).toFixed(2)}`;
  }

  return (
    <div className="logs-container">
      <div className="logs-header">
        <h2>Request Logs</h2>
        <p>View every API call made with your keys</p>
      </div>

      <div className="logs-filters">
        <select
          className="workbench-select"
          value={filters.primitiveId}
          onChange={(e) => setFilters(prev => ({ ...prev, primitiveId: e.target.value, page: 1 }))}
        >
          <option value="">All primitives</option>
          {PRIMITIVES.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        <select
          className="workbench-select"
          value={filters.statusCode}
          onChange={(e) => setFilters(prev => ({ ...prev, statusCode: e.target.value, page: 1 }))}
        >
          <option value="">All statuses</option>
          <option value="2xx">Success (2xx)</option>
          <option value="4xx">Error (4xx)</option>
          <option value="5xx">Server Error (5xx)</option>
        </select>
      </div>

      {loading ? (
        <LogsTabSkeleton />
      ) : logs.length === 0 ? (
        <div className="workbench-empty">
          <FileText size={48} />
          <h3>No requests yet</h3>
          <p>Make your first API call from the Explorer.</p>
        </div>
      ) : (
        <>
          <div className="workbench-card logs-table-container">
            <table className="workbench-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Primitive</th>
                  <th>Status</th>
                  <th>Latency</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr
                      onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{formatTimestamp(log.created_at)}</td>
                      <td><code className="workbench-code-inline">{log.primitive_id || '—'}</code></td>
                      <td>
                        <span className={`workbench-badge ${
                          log.status_code >= 200 && log.status_code < 300 ? 'workbench-badge-success' :
                          log.status_code >= 400 && log.status_code < 500 ? 'workbench-badge-warning' :
                          'workbench-badge-error'
                        }`}>
                          {log.status_code}
                        </span>
                      </td>
                      <td>{log.response_time_ms != null ? `${log.response_time_ms}ms` : '—'}</td>
                      <td>{formatCost(log.cost_cents)}</td>
                    </tr>
                    {expandedRow === log.id && (
                      <tr className="expanded-row">
                        <td colSpan={5}>
                          <div className="expanded-content">
                            <div className="expanded-item">
                              <span className="expanded-label">Endpoint</span>
                              <code className="workbench-code-inline">{log.method} {log.endpoint}</code>
                            </div>
                            <div className="expanded-item">
                              <span className="expanded-label">IP Address</span>
                              <span>{log.request_ip || '—'}</span>
                            </div>
                            <div className="expanded-item">
                              <span className="expanded-label">Request ID</span>
                              <code style={{ fontSize: '11px' }}>{log.id}</code>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="logs-pagination">
            <button
              className="workbench-btn workbench-btn-secondary workbench-btn-sm"
              disabled={filters.page <= 1}
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              Previous
            </button>
            <span className="pagination-info">Page {filters.page} of {totalPages}</span>
            <button
              className="workbench-btn workbench-btn-secondary workbench-btn-sm"
              disabled={filters.page >= totalPages}
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
